import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/db";
import { api } from "@/lib/api";
import { useMemberCategories } from "@/lib/member-categories";

import { useAuth } from "@/lib/auth-context";
import { Guard } from "@/components/Guard";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Pencil, Trash2, History, Loader2, FileDown, FileText, AlertTriangle, RotateCcw, Archive, ArchiveRestore, Check, X, CalendarClock } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
// jsPDF chargé dynamiquement dans exportEmargementPDF (cf. plus bas)

export const Route = createFileRoute("/members")({
  validateSearch: (s: Record<string, unknown>) => ({ new: s.new === "1" ? "1" : undefined }) as { new?: "1" },
  component: () => <Guard module="members"><MembersPage /></Guard>,
});

// Les catégories d'adhérents (et leurs montants) sont paramétrées dans la trésorerie
type MemberCategory = string;

interface Member {
  id: string; first_name: string; last_name: string;
  email: string | null; phone: string | null;
  address_line1: string | null; address_line2: string | null;
  postal_code: string | null; city: string | null; country: string | null;
  birth_date: string | null; profession: string | null; notes: string | null;
  category?: MemberCategory;
  co_owners_count?: number | null;
  copropriete_id?: string | null;
  copropriete_name?: string | null;
  archived?: boolean; archived_at?: string | null; archived_reason?: string | null;
}

const INACTIVITY_YEARS = 3;
const normalizeSeason = (season?: string | null) => (season ?? "").trim().match(/^(\d{4})(?:\s*-\s*\d{4})?$/)?.[1] ?? (season ?? "").trim();

function MembersPage() {
  const { hasAccess } = useAuth();
  const canWrite = hasAccess("members", "write");
  const canDelete = hasAccess("members", "admin");
  const qc = useQueryClient();
  const routeSearch = Route.useSearch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Partial<Member> | null>(null);
  const [open, setOpen] = useState(false);
  const [historyMember, setHistoryMember] = useState<Member | null>(null);

  // Cotisation initiale (uniquement à la création d'un nouvel adhérent)
  const currentSeasonDefault = String(new Date().getFullYear());
  const [createWithMembership, setCreateWithMembership] = useState(true);
  const [initSeason, setInitSeason] = useState(currentSeasonDefault);
  const [initAmount, setInitAmount] = useState("20");
  const [initPaidAt, setInitPaidAt] = useState(format(new Date(), "yyyy-MM-dd"));
  const [initMethod, setInitMethod] = useState<"Espèces" | "Chèque">("Espèces");

  const currentYear = new Date().getFullYear();
  
  const [seasonFilter, setSeasonFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<"all" | MemberCategory>("all");
  const {
    categories: memberCategories,
    labelOf: categoryLabel,
    isCopropriete: isCoproprieteCategory,
    feeOf: categoryFee,
    normalize: normalizeCategory,
  } = useMemberCategories();
  const [tab, setTab] = useState<"active" | "inactive">("active");

  // Sidebar shortcut "Ajouter un adhérent"
  useEffect(() => {
    if (routeSearch.new === "1" && canWrite) {
      const defaultCat = normalizeCategory(null);
      setEditing({ category: defaultCat });
      setInitAmount(feeFor(defaultCat));
      setOpen(true);
      navigate({ to: "/members", search: {}, replace: true });
    }
  }, [routeSearch.new, canWrite, navigate]);

  const { data: orgSettings } = useQuery({
    queryKey: ["org_settings"],
    queryFn: () =>
      api.get<{
        fee_individual?: number | string | null;
        fee_copropriete?: number | string | null;
        fee_professionnel?: number | string | null;
        season_filter_years?: number | string | null;
        season_filter_start_year?: number | string | null;
      }>("/api/org-settings"),
  });
  const seasonYears = (() => {
    const n = Number(orgSettings?.season_filter_years);
    return Number.isInteger(n) && n >= 1 && n <= 30 ? n : 5;
  })();
  const seasonStartYear = (() => {
    const n = Number(orgSettings?.season_filter_start_year);
    return Number.isInteger(n) && n >= 1900 && n <= 2200 ? n : currentYear;
  })();
  const seasonOptions: string[] = Array.from({ length: seasonYears }, (_, i) => String(seasonStartYear - i));
  const feeFor = (category: MemberCategory) => {
    const fee = categoryFee(category);
    return fee !== "0" ? fee : "20";
  };

  type MemberWithMs = Member & {
    memberships: { id: string; season: string; amount: number; paid_at: string | null; payment_method: string | null }[];
  };

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("*, memberships(id, season, amount, paid_at, payment_method)")
        .order("last_name");
      if (error) throw error;
      return (data ?? []) as MemberWithMs[];
    },
  });

  const { data: optionsByMember = {} } = useQuery({
    queryKey: ["member-options"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("transactions")
        .select("member_id, description, amount")
        .eq("external_source", "helloasso")
        .not("membership_id", "is", null)
        .like("description", "Option · %");
      if (error) throw error;
      const map: Record<string, { name: string; amount: number }[]> = {};
      for (const t of data ?? []) {
        if (!t.member_id) continue;
        const name = (t.description ?? "").replace(/^Option · /, "");
        (map[t.member_id] ??= []).push({ name, amount: Number(t.amount) });
      }
      return map;
    },
  });

  // Per-member: last cotisation + "à jour" (cotisation pour l'année civile en cours)
  const inactivityThreshold = currentYear - INACTIVITY_YEARS;
  const enriched = members.map((m) => {
    const ms = [...(m.memberships ?? [])]
      .map((x) => ({ ...x, season: normalizeSeason(x.season) }))
      .sort((a, b) => {
      const da = a.paid_at ?? `${a.season}-01-01`;
      const db = b.paid_at ?? `${b.season}-01-01`;
      return db.localeCompare(da);
    });
    // Fallback : si l'API n'a pas inclus le tableau memberships[],
    // utilise le champ last_season renvoyé par /api/members.
    const fallbackSeason = normalizeSeason((m as any).last_season ?? null);
    const fallbackPaymentMethod = ((m as any).last_payment_method ?? null) as string | null;
    const fallbackAmountRaw = (m as any).last_amount;
    const fallbackAmount = Number.isFinite(Number(fallbackAmountRaw)) ? Number(fallbackAmountRaw) : 0;
    const fallbackPaidAt = ((m as any).last_paid_at ?? null) as string | null;
    let last = ms[0] ?? null;
    if (!last && fallbackSeason) {
      last = { id: "", season: fallbackSeason, amount: fallbackAmount, paid_at: fallbackPaidAt, payment_method: fallbackPaymentMethod };
    } else if (last) {
      last = {
        ...last,
        payment_method: last.payment_method || fallbackPaymentMethod,
        amount: Number.isFinite(Number(last.amount)) && Number(last.amount) > 0 ? Number(last.amount) : fallbackAmount,
        paid_at: last.paid_at ?? fallbackPaidAt,
      };
    }
    const upToDate = ms.some((x) => x.season === String(currentYear))
      || (ms.length === 0 && fallbackSeason === String(currentYear));
    const seasons = new Set<string>(ms.map((x) => x.season));
    if (ms.length === 0 && fallbackSeason) seasons.add(fallbackSeason);
    const lastSeasonNum = last ? Number(last.season) : 0;
    // Suggéré inactif si dernière cotis < seuil OU jamais cotisé
    const inactivitySuggested = !last || lastSeasonNum < inactivityThreshold;
    const isInactive = !!m.archived || (inactivitySuggested && !m.archived);
    // Note: archived prend le dessus, sinon "suggéré" = inactif
    return { ...m, _last: last, _upToDate: upToDate, _seasons: seasons, _inactivitySuggested: inactivitySuggested, _isInactive: isInactive };
  });

  const filtered = enriched
    .filter((m) => (tab === "active" ? !m._isInactive : m._isInactive))
    .filter((m) => {
      const q = search.toLowerCase();
      const matchSearch = !q || `${m.first_name} ${m.last_name} ${m.email ?? ""}`.toLowerCase().includes(q);
      const matchSeason = seasonFilter === "all" || m._seasons.has(seasonFilter);
      const matchCategory = categoryFilter === "all" || normalizeCategory(m.category) === categoryFilter;
      return matchSearch && matchSeason && matchCategory;
    })
    .sort((a, b) => {
      // Onglet inactifs : tri par dernière cotisation (les plus anciens d'abord)
      if (tab === "inactive") {
        const la = a._last ? Number(a._last.season) : 0;
        const lb = b._last ? Number(b._last.season) : 0;
        if (la !== lb) return la - lb;
      }
      return a.last_name.localeCompare(b.last_name);
    });

  const coproprieteOptions = members
    .filter((m) => isCoproprieteCategory(normalizeCategory(m.category)))
    .sort((a, b) => a.last_name.localeCompare(b.last_name));

  const activeCount = enriched.filter((m) => !m._isInactive).length;
  const inactiveCount = enriched.filter((m) => m._isInactive).length;

  const exportCSV = () => {
    const headers = ["Nom", "Prénom", "Catégorie", "Nb copropriétaires", "Copropriété de rattachement", "Email", "Téléphone", "Adresse", "Complément", "CP", "Ville", "Pays", "Date naissance", "À jour", "Dernière cotisation (saison)", "Dernière cotisation (date)", "Dernière cotisation (montant €)", "Notes"];
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filtered.map((m) => [
      m.last_name, m.first_name,
      categoryLabel(normalizeCategory(m.category)),
      m.co_owners_count ?? "", m.copropriete_name ?? "",
      m.email, m.phone,
      m.address_line1, m.address_line2, m.postal_code, m.city, m.country, m.birth_date,
      m._upToDate ? "Oui" : "Non",
      m._last?.season ?? "", m._last?.paid_at ?? "", m._last ? Number(m._last.amount).toFixed(2) : "",
      m.notes,
    ].map(esc).join(","));
    const csv = "\uFEFF" + headers.map(esc).join(",") + "\n" + rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `adherents_${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filtered.length} adhérent(s) exporté(s)`);
  };

  const exportEmargementPDF = async () => {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);
    const doc = new jsPDF({ orientation: "landscape" });
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFontSize(16);
    doc.text("Feuille d'émargement", 14, 18);
    doc.setFontSize(10);
    const subtitle = seasonFilter === "all" ? `Saison : toutes` : `Saison : ${seasonFilter}`;
    doc.text(`${subtitle} — ${filtered.length} adhérent(s) — ${format(new Date(), "dd/MM/yyyy")}`, 14, 25);
    doc.setDrawColor(200);
    doc.line(14, 29, pageW - 14, 29);

    autoTable(doc, {
      startY: 34,
      head: [["#", "Nom", "Prénom", "Téléphone", "Dernière cotis.", "Date", "Montant (€)", "Signature"]],
      body: filtered.map((m, i) => [
        String(i + 1),
        m.last_name,
        m.first_name,
        m.phone ?? "",
        m._last?.season ?? "—",
        m._last?.paid_at ? format(new Date(m._last.paid_at), "dd/MM/yyyy") : "—",
        m._last ? Number(m._last.amount).toFixed(2) : "—",
        "",
      ]),
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [40, 40, 40] },
      columnStyles: {
        0: { cellWidth: 10 },
        7: { cellWidth: 60 },
      },
      didDrawPage: () => {
        const pageCount = (doc as unknown as { internal: { getNumberOfPages: () => number } }).internal.getNumberOfPages();
        const current = (doc as unknown as { internal: { getCurrentPageInfo: () => { pageNumber: number } } }).internal.getCurrentPageInfo().pageNumber;
        doc.setFontSize(8);
        doc.text(`Page ${current} / ${pageCount}`, pageW - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
      },
    });
    doc.save(`emargement_${seasonFilter === "all" ? "toutes" : seasonFilter}_${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  const save = useMutation({
    mutationFn: async (m: Partial<Member>) => {
      const payload = {
        first_name: m.first_name ?? "", last_name: m.last_name ?? "",
        email: m.email || null, phone: m.phone || null,
        address_line1: m.address_line1 || null, address_line2: m.address_line2 || null,
        postal_code: m.postal_code || null, city: m.city || null, country: m.country || "France",
        birth_date: m.birth_date || null, profession: m.profession || null, notes: m.notes || null,
        category: normalizeCategory(m.category),
        co_owners_count:
          isCoproprieteCategory(normalizeCategory(m.category)) && m.co_owners_count !== null && m.co_owners_count !== undefined && String(m.co_owners_count) !== ""
            ? Number(m.co_owners_count)
            : null,
        copropriete_id: isCoproprieteCategory(normalizeCategory(m.category)) ? null : (m.copropriete_id || null),
      };
      if (m.id) {
        // On appelle directement l'API PHP pour pouvoir vérifier la ligne réellement enregistrée
        const res = await api.patch<any>(`/api/members/${encodeURIComponent(m.id)}`, payload, { rawJson: true });
        const saved = (res?.data ?? res) as Record<string, unknown> | null;
        if (!saved || typeof saved !== "object" || !saved.id) {
          throw new Error("Modification non enregistrée : réponse inattendue du serveur.");
        }
        const norm = (v: unknown) => (v === null || v === undefined || v === "" ? "" : String(v).trim());
        const mismatched = Object.entries(payload)
          .filter(([k, v]) => norm((saved as any)[k]) !== norm(v))
          .map(([k]) => k);
        if (mismatched.length > 0) {
          throw new Error(`Modification non enregistrée pour : ${mismatched.join(", ")} (le serveur a renvoyé les anciennes valeurs).`);
        }
        return { createdMembership: false };
      }

      const { data: inserted, error } = await supabase.from("members").insert([payload]).select("*").single();
      if (error) throw error;
      // Cotisation initiale optionnelle
      if (createWithMembership && inserted?.id) {
        const amt = Number(initAmount);
        if (!Number.isFinite(amt) || amt <= 0) throw new Error("Montant de cotisation invalide");
        const { error: e2 } = await supabase.from("memberships").insert({
          member_id: inserted.id,
          season: normalizeSeason(initSeason) || currentSeasonDefault,
          amount: amt,
          paid_at: initPaidAt || null,
          payment_method: initMethod,
        });
        if (e2) throw e2;
        return { createdMembership: true };
      }
      return { createdMembership: false };
    },
    onSuccess: async (r) => {
      // refetch forcé (invalidate seul peut laisser le cache affiché)
      await qc.refetchQueries({ queryKey: ["members"], exact: true });
      qc.refetchQueries({ queryKey: ["member-options"], exact: true });
      qc.invalidateQueries({ queryKey: ["transactions"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success(r?.createdMembership ? "Adhérent et cotisation enregistrés" : "Adhérent enregistré");
      setOpen(false); setEditing(null);
      // reset form options
      setCreateWithMembership(true);
      setInitSeason(currentSeasonDefault);
      setInitAmount(feeFor("individual"));
      setInitPaidAt(format(new Date(), "yyyy-MM-dd"));
      setInitMethod("Espèces");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const del = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("members").delete().eq("id", id);
      if (error) {
        const err = new Error(error.message) as Error & { detail?: string };
        err.detail = (error as any).detail;
        throw err;
      }
    },
    onSuccess: () => {
      // Toutes les vues qui listent des adhérents (adhérents, listes de diffusion…)
      qc.invalidateQueries({ queryKey: ["members"] });
      qc.invalidateQueries({ queryKey: ["members-with-status"] });
      qc.invalidateQueries({ queryKey: ["mailing_list_members"] });
      qc.invalidateQueries({ queryKey: ["mailing_list_counts"] });
      toast.success("Adhérent supprimé");
    },
    onError: (e: any) =>
      toast.error(e?.message ?? "Suppression impossible", {
        description: e?.detail ?? "Aucun détail renvoyé par le serveur",
        duration: 15000,
      }),
  });

  const archive = useMutation({
    mutationFn: async (vars: { id: string; reason?: string | null }) => {
      const { error } = await supabase
        .from("members")
        .update({ archived: true, archived_at: new Date().toISOString(), archived_reason: vars.reason || null } as any)
        .eq("id", vars.id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["members"] }); toast.success("Adhérent archivé"); },
    onError: (e: any) => toast.error(e.message),
  });

  const restore = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("members")
        .update({ archived: false, archived_at: null, archived_reason: null } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["members"] }); toast.success("Adhérent réactivé"); },
    onError: (e: any) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader
        title="Adhérents"
        description="Coordonnées et historique des cotisations"
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={exportCSV}>
              <FileDown className="h-4 w-4 mr-2" />Export CSV
            </Button>
            <Button variant="outline" onClick={exportEmargementPDF}>
              <FileText className="h-4 w-4 mr-2" />Émargement PDF
            </Button>
            {canWrite && (
              <Button onClick={() => { const c = normalizeCategory(null); setEditing({ category: c }); setInitAmount(feeFor(c)); setOpen(true); }}>
                <Plus className="h-4 w-4 mr-2" />Ajouter
              </Button>
            )}
          </div>
        }
      />
      <Tabs value={tab} onValueChange={(v) => setTab(v as "active" | "inactive")} className="mb-4">
        <TabsList>
          <TabsTrigger value="active">Actifs <Badge variant="secondary" className="ml-2">{activeCount}</Badge></TabsTrigger>
          <TabsTrigger value="inactive">Inactifs <Badge variant="secondary" className="ml-2">{inactiveCount}</Badge></TabsTrigger>
        </TabsList>
      </Tabs>
      <Card className="p-4 mb-4">
        <div className="flex gap-3 flex-wrap items-center">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Rechercher par nom, prénom, email…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
          </div>
          <Select value={categoryFilter} onValueChange={(v) => setCategoryFilter(v as typeof categoryFilter)}>
            <SelectTrigger className="w-[200px]"><SelectValue placeholder="Catégorie" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les catégories</SelectItem>
              {memberCategories.map((c) => (
                <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={seasonFilter} onValueChange={setSeasonFilter}>
            <SelectTrigger className="w-[220px]"><SelectValue placeholder="Saison" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{`Toutes les saisons (${seasonYears} ans depuis ${seasonStartYear})`}</SelectItem>
              {seasonOptions.map((s) => (
                <SelectItem key={s} value={s}>Saison {s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]"></TableHead>
              <TableHead>Nom</TableHead>
              <TableHead>Prénom</TableHead>
              <TableHead>Catégorie</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Adresse</TableHead>
              <TableHead>Dernière cotis.</TableHead>
              <TableHead>Mode de paiement</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground"><Loader2 className="inline animate-spin h-4 w-4 mr-2" />Chargement…</TableCell></TableRow>}
            {!isLoading && filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Aucun adhérent</TableCell></TableRow>}
            {filtered.map((m) => {
              const fromHA = m.memberships?.some((ms) => ms.payment_method === "HelloAsso");
              const addressParts = [m.address_line1, m.postal_code && m.city ? `${m.postal_code} ${m.city}` : (m.postal_code || m.city)].filter(Boolean);
              const options = optionsByMember[m.id] ?? [];
              return (
              <TableRow key={m.id}>
                <TableCell>
                  {m.archived ? (
                    <span title="Archivé manuellement" className="inline-block h-3 w-3 rounded-full bg-muted-foreground" />
                  ) : (
                    <span
                      title={m._upToDate ? `À jour (saison ${currentYear})` : `Non à jour pour ${currentYear}`}
                      className={`inline-block h-3 w-3 rounded-full ${m._upToDate ? "bg-emerald-500" : "bg-destructive"}`}
                    />
                  )}
                </TableCell>
                <TableCell className="font-medium">
                  {m.last_name}
                  {m.archived && <Badge variant="outline" className="ml-2 text-xs">Archivé</Badge>}
                </TableCell>
                <TableCell>{m.first_name}</TableCell>
                <TableCell className="text-xs">
                  {isCoproprieteCategory(normalizeCategory(m.category)) ? (
                    <div className="space-y-0.5">
                      <Badge variant="secondary">Copropriété</Badge>
                      {m.co_owners_count ? <div className="text-muted-foreground">{m.co_owners_count} copropriétaire(s)</div> : null}
                    </div>
                  ) : (
                    <div className="space-y-0.5">
                      <Badge variant="outline">{categoryLabel(normalizeCategory(m.category))}</Badge>
                      {m.copropriete_name ? <div className="text-muted-foreground">via {m.copropriete_name}</div> : null}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{m.email}</TableCell>
                <TableCell className="text-muted-foreground text-xs">
                  {addressParts.length > 0 ? addressParts.join(" — ") : "—"}
                </TableCell>
                <TableCell className="text-xs">
                  {m._last ? (
                    <div>
                      <div className="font-medium">Saison {m._last.season}</div>
                      <div className="text-muted-foreground">
                        {m._last.paid_at ? format(new Date(m._last.paid_at), "dd/MM/yyyy") : "—"} · {Number(m._last.amount).toFixed(2)} €
                      </div>
                    </div>
                  ) : <span className="text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-xs">
                  {m._last?.payment_method ? (
                    m._last.payment_method === "HelloAsso" ? (
                      <Badge variant="secondary" className="bg-accent/15 text-accent border-accent/30">HelloAsso</Badge>
                    ) : (
                      <span>{m._last.payment_method}</span>
                    )
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setHistoryMember(m)} title="Historique cotisations">
                    <History className="h-4 w-4" />
                  </Button>
                  {canWrite && (
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(m); setOpen(true); }} title="Modifier">
                      <Pencil className="h-4 w-4" />
                    </Button>
                  )}
                  {canWrite && tab === "active" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      title={m._inactivitySuggested ? `Inactif suggéré (dernière cotis. ${m._last?.season ?? "—"})` : "Archiver l'adhérent"}
                      onClick={() => {
                        const reason = m._inactivitySuggested
                          ? `Inactif (dernière cotisation: ${m._last?.season ?? "aucune"})`
                          : "";
                        if (confirm(`Archiver ${m.first_name} ${m.last_name} ?\n\nLes données et l'historique de cotisations seront conservés.`)) {
                          archive.mutate({ id: m.id, reason });
                        }
                      }}
                    >
                      <Archive className={`h-4 w-4 ${m._inactivitySuggested ? "text-warning" : ""}`} />
                    </Button>
                  )}
                  {canWrite && tab === "inactive" && m.archived && (
                    <Button
                      variant="ghost"
                      size="sm"
                      title="Réactiver l'adhérent"
                      onClick={() => restore.mutate(m.id)}
                    >
                      <ArchiveRestore className="h-4 w-4 text-emerald-600" />
                    </Button>
                  )}
                  {canDelete && (
                    <Button variant="ghost" size="sm" onClick={() => { if (confirm(`Supprimer définitivement ${m.first_name} ${m.last_name} ?`)) del.mutate(m.id); }} title="Supprimer">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto w-[calc(100%-1rem)] sm:w-full">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifier l'adhérent" : "Nouvel adhérent"}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={(e) => { e.preventDefault(); save.mutate(editing); }} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Prénom *"><Input required value={editing.first_name ?? ""} onChange={(e) => setEditing({ ...editing, first_name: e.target.value })} /></Field>
                <Field label="Nom *"><Input required value={editing.last_name ?? ""} onChange={(e) => setEditing({ ...editing, last_name: e.target.value })} /></Field>
                <Field label="Email"><Input type="email" value={editing.email ?? ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} /></Field>
                <Field label="Téléphone"><Input value={editing.phone ?? ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Catégorie d'adhérent *">
                  <Select
                    value={normalizeCategory(editing.category)}
                    onValueChange={(v) => {
                      const cat = normalizeCategory(v);
                      const copro = isCoproprieteCategory(cat);
                      setEditing({
                        ...editing,
                        category: cat,
                        co_owners_count: copro ? editing.co_owners_count ?? null : null,
                        copropriete_id: copro ? null : editing.copropriete_id ?? null,
                      });
                      if (!editing.id) setInitAmount(feeFor(cat));
                    }}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {memberCategories.map((c) => (
                        <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {isCoproprieteCategory(normalizeCategory(editing.category)) ? (
                  <Field label="Nombre de copropriétaires">
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={editing.co_owners_count ?? ""}
                      onChange={(e) => setEditing({ ...editing, co_owners_count: e.target.value === "" ? null : Number(e.target.value) })}
                    />
                  </Field>
                ) : (
                  <Field label="Copropriété de rattachement (optionnel)">
                    <Select
                      value={editing.copropriete_id ?? "none"}
                      onValueChange={(v) => setEditing({ ...editing, copropriete_id: v === "none" ? null : v })}
                    >
                      <SelectTrigger><SelectValue placeholder="Aucune" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— Aucune —</SelectItem>
                        {coproprieteOptions
                          .filter((c) => c.id !== editing.id)
                          .map((c) => (
                            <SelectItem key={c.id} value={c.id}>{`${c.last_name} ${c.first_name}`.trim()}</SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
              </div>
              <Field label="Adresse"><Input value={editing.address_line1 ?? ""} onChange={(e) => setEditing({ ...editing, address_line1: e.target.value })} /></Field>
              <Field label="Complément"><Input value={editing.address_line2 ?? ""} onChange={(e) => setEditing({ ...editing, address_line2: e.target.value })} /></Field>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Field label="Code postal"><Input value={editing.postal_code ?? ""} onChange={(e) => setEditing({ ...editing, postal_code: e.target.value })} /></Field>
                <Field label="Ville"><Input value={editing.city ?? ""} onChange={(e) => setEditing({ ...editing, city: e.target.value })} /></Field>
                <Field label="Pays" className="col-span-2 sm:col-span-1"><Input value={editing.country ?? "France"} onChange={(e) => setEditing({ ...editing, country: e.target.value })} /></Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Date de naissance"><Input type="date" value={editing.birth_date ?? ""} onChange={(e) => setEditing({ ...editing, birth_date: e.target.value })} /></Field>
                <Field label="Profession"><Input value={editing.profession ?? ""} onChange={(e) => setEditing({ ...editing, profession: e.target.value })} /></Field>
              </div>
              <Field label="Notes"><Textarea value={editing.notes ?? ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field>

              {!editing.id && (
                <div className="border-t pt-3 mt-2 space-y-3">
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="create-with-cotisation"
                      checked={createWithMembership}
                      onCheckedChange={(v) => setCreateWithMembership(v === true)}
                      className="mt-0.5 shrink-0"
                    />
                    <Label htmlFor="create-with-cotisation" className="cursor-pointer font-medium">
                      Enregistrer une cotisation pour ce nouvel adhérent
                    </Label>
                  </div>
                  {createWithMembership && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Field label="Saison">
                        <Input value={initSeason} onChange={(e) => setInitSeason(e.target.value)} />
                      </Field>
                      <Field label="Montant (€)">
                        <Input type="number" step="0.01" min="0" value={initAmount} onChange={(e) => setInitAmount(e.target.value)} />
                      </Field>
                      <Field label="Date de paiement">
                        <Input type="date" value={initPaidAt} onChange={(e) => setInitPaidAt(e.target.value)} />
                      </Field>
                      <Field label="Mode de paiement">
                        <Select value={initMethod} onValueChange={(v) => setInitMethod(v as "Espèces" | "Chèque")}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Espèces">Espèces</SelectItem>
                            <SelectItem value="Chèque">Chèque</SelectItem>
                          </SelectContent>
                        </Select>
                      </Field>
                    </div>
                  )}
                </div>
              )}

              <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="w-full sm:w-auto">Annuler</Button>
                <Button type="submit" disabled={save.isPending} className="w-full sm:w-auto">{save.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enregistrer</Button>
              </DialogFooter>

            </form>
          )}
        </DialogContent>
      </Dialog>

      <MembershipHistoryDialog member={historyMember} onClose={() => setHistoryMember(null)} canWrite={canWrite} />
    </>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return <div className={`space-y-1.5 ${className ?? ""}`}><Label>{label}</Label>{children}</div>;
}


const NSF_TAG = "[NSF]";
const isNsf = (notes?: string | null) => !!notes && notes.includes(NSF_TAG);
const stripNsf = (notes?: string | null) => (notes ?? "").replace(NSF_TAG, "").trim();

function MembershipHistoryDialog({ member, onClose, canWrite }: { member: Member | null; onClose: () => void; canWrite: boolean }) {
  const qc = useQueryClient();
  const [season, setSeason] = useState(String(new Date().getFullYear()));
  const [amount, setAmount] = useState("20");
  const [paidAt, setPaidAt] = useState(format(new Date(), "yyyy-MM-dd"));
  const [method, setMethod] = useState<"Espèces" | "Chèque">("Espèces");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editSeason, setEditSeason] = useState("");
  const [editAmount, setEditAmount] = useState("");

  const { data: history = [] } = useQuery({
    queryKey: ["memberships", member?.id],
    queryFn: async () => {
      if (!member) return [];
      const { data, error } = await supabase.from("memberships").select("*").eq("member_id", member.id).order("season", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!member,
  });

  const add = useMutation({
    mutationFn: async () => {
      if (!member) return;
      const amt = Number(amount);
      if (!Number.isFinite(amt) || amt <= 0) throw new Error("Montant invalide");
      const { error } = await supabase.from("memberships").insert({
        member_id: member.id, season: normalizeSeason(season), amount: amt,
        paid_at: paidAt || null, payment_method: method,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["memberships", member?.id] });
      qc.invalidateQueries({ queryKey: ["members"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      qc.invalidateQueries({ queryKey: ["transactions"] });
      toast.success("Cotisation enregistrée (recette créée automatiquement)");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const updateSeason = useMutation({
    mutationFn: async ({ id, value, amountValue }: { id: string; value: string; amountValue: string }) => {
      const s = normalizeSeason(value);
      if (!/^\d{4}$/.test(s)) throw new Error("Saison invalide (année sur 4 chiffres)");
      const a = Number(String(amountValue).replace(",", "."));
      if (!Number.isFinite(a) || a < 0) throw new Error("Montant invalide");
      const { error } = await supabase.from("memberships").update({ season: s, amount: a }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      setEditingId(null);
      qc.invalidateQueries({ queryKey: ["memberships", member?.id] });
      qc.invalidateQueries({ queryKey: ["members"] });
      qc.invalidateQueries({ queryKey: ["members-with-status"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Cotisation mise à jour");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const toggleNsf = useMutation({
    mutationFn: async (row: { id: string; notes: string | null; nsf: boolean }) => {
      const newNotes = row.nsf
        ? stripNsf(row.notes) || null
        : `${NSF_TAG} ${stripNsf(row.notes)}`.trim();
      const { error } = await supabase.from("memberships").update({ notes: newNotes }).eq("id", row.id);
      if (error) throw error;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["memberships", member?.id] });
      qc.invalidateQueries({ queryKey: ["members"] });
      toast.success(vars.nsf ? "Chèque marqué comme provisionné" : "Chèque marqué sans provision");
    },
    onError: (e: any) => toast.error(e.message),
  });

  const infoRows: { label: string; value: string | null | undefined }[] = member ? [
    { label: "Email", value: member.email },
    { label: "Téléphone", value: member.phone },
    { label: "Date de naissance", value: member.birth_date },
    { label: "Profession", value: member.profession },
    { label: "Adresse", value: [member.address_line1, member.address_line2].filter(Boolean).join(" ") || null },
    { label: "Ville", value: [member.postal_code, member.city].filter(Boolean).join(" ") || null },
    { label: "Pays", value: member.country },
    { label: "Notes", value: member.notes },
  ] : [];

  return (
    <Dialog open={!!member} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Fiche adhérent — {member?.first_name} {member?.last_name}</DialogTitle></DialogHeader>
        {member && (
          <div className="rounded-md border bg-muted/30 p-3 mb-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
              {infoRows.map((row) => (
                <div key={row.label} className="flex gap-2">
                  <span className="text-muted-foreground min-w-[120px]">{row.label} :</span>
                  <span className="font-medium break-words">{row.value || "—"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <h4 className="font-medium mt-2">Historique des cotisations</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Saison</TableHead>
              <TableHead>Montant</TableHead>
              <TableHead>Payé le</TableHead>
              <TableHead>Mode</TableHead>
              <TableHead>Statut</TableHead>
              {canWrite && <TableHead className="text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {history.length === 0 && <TableRow><TableCell colSpan={canWrite ? 6 : 5} className="text-center text-muted-foreground py-4">Aucune cotisation</TableCell></TableRow>}
            {history.map((h: any) => {
              const nsf = isNsf(h.notes);
              const pm = (h.payment_method ?? "").toLowerCase();
              const isCheque = pm.includes("chèque") || pm.includes("cheque");
              const isManual = isCheque || pm.includes("espèce") || pm.includes("espece") || pm.includes("cash") || pm === "";
              const editing = editingId === h.id;
              return (
                <TableRow key={h.id} className={nsf ? "bg-destructive/5" : ""}>
                  <TableCell>
                    {editing ? (
                      <Input
                        className="h-8 w-20"
                        value={editSeason}
                        onChange={(e) => setEditSeason(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") updateSeason.mutate({ id: h.id, value: editSeason, amountValue: editAmount }); }}
                      />
                    ) : (
                      normalizeSeason(h.season)
                    )}
                  </TableCell>
                  <TableCell>
                    {editing ? (
                      <div className="flex items-center gap-1">
                        <Input
                          className="h-8 w-24"
                          type="number"
                          step="0.01"
                          min="0"
                          value={editAmount}
                          onChange={(e) => setEditAmount(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") updateSeason.mutate({ id: h.id, value: editSeason, amountValue: editAmount }); }}
                        />
                        <Button size="sm" variant="ghost" onClick={() => updateSeason.mutate({ id: h.id, value: editSeason, amountValue: editAmount })} disabled={updateSeason.isPending} title="Enregistrer">
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} title="Annuler">
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      `${Number(h.amount).toFixed(2)} €`
                    )}
                  </TableCell>
                  <TableCell>{h.paid_at ?? "—"}</TableCell>
                  <TableCell>{h.payment_method ?? "—"}</TableCell>
                  <TableCell>
                    {nsf ? (
                      <Badge variant="destructive" className="gap-1">
                        <AlertTriangle className="h-3 w-3" />Sans provision
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-500/30">OK</Badge>
                    )}
                  </TableCell>
                  {canWrite && (
                    <TableCell className="text-right">
                      {isManual && !editing && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setEditingId(h.id); setEditSeason(normalizeSeason(h.season)); setEditAmount(String(Number(h.amount ?? 0))); }}
                          title="Modifier l'année de validité et le montant"
                        >
                          <CalendarClock className="h-4 w-4" />
                        </Button>
                      )}
                      {isCheque && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleNsf.mutate({ id: h.id, notes: h.notes, nsf })}
                          title={nsf ? "Annuler le marquage sans provision" : "Marquer chèque sans provision"}
                        >
                          {nsf ? <RotateCcw className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4 text-destructive" />}
                        </Button>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {canWrite && (
          <div className="border-t pt-4 mt-2">
            <h4 className="font-medium mb-3">Ajouter une cotisation</h4>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Saison"><Input value={season} onChange={(e) => setSeason(e.target.value)} /></Field>
              <Field label="Montant (€)"><Input type="number" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
              <Field label="Date de paiement"><Input type="date" value={paidAt} onChange={(e) => setPaidAt(e.target.value)} /></Field>
              <Field label="Mode de paiement">
                <Select value={method} onValueChange={(v) => setMethod(v as "Espèces" | "Chèque")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Espèces">Espèces</SelectItem>
                    <SelectItem value="Chèque">Chèque</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Button className="mt-3 w-full" onClick={() => add.mutate()} disabled={add.isPending}>
              {add.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enregistrer la cotisation
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

